import { getFormProps, useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { useFetcher } from 'react-router'
import { useTranslation } from 'react-i18next'
import { getIconName } from '~/components/icon/icon.helper'
import { Icon } from '~/components/icon/icon'
import Text from '~/components/text/text'
import { useTheme } from '~/hooks/useTheme'
import { cn } from '~/utils/misc'
import { ThemeFormSchema } from '~/types/setTheme'

interface ThemeSwitchProps {
	wide?: '1' | '2' | '3'
	mobile?: boolean
}

export function ThemeSwitch({ wide, mobile }: ThemeSwitchProps) {
	const fetcher = useFetcher()
	const { t } = useTranslation()
	const [form] = useForm({
		id: 'theme-switch',
		lastResult: fetcher?.data?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ThemeFormSchema })
		},
	})

	const mode = useTheme()
	const nextMode = mode === 'light' ? 'dark' : 'light'

	if (mobile) {
		return (
			<fetcher.Form
				method="POST"
				action="/api/set-theme"
				{...getFormProps(form)}
			>
				<input type="hidden" name="theme" value={nextMode} />
				<button
					type="submit"
					className="bg-background border-border hover:bg-muted/50 flex w-full items-center justify-between rounded-lg border p-3 transition-colors"
					title={t('action_change_theme')}
				>
					<div className="flex items-center gap-3">
						<div className="bg-muted flex h-8 w-8 items-center justify-center rounded-md">
							<Icon
								name={getIconName(mode === 'dark' ? 'sun' : 'moon')}
								size="sm"
							/>
						</div>
						<div className="text-left">
							<Text as="sm" className="font-medium">
								{mode === 'dark' ? t('theme_dark') : t('theme_light')}
							</Text>
							<Text as="xs" color="light">
								{t('action_change_theme')}
							</Text>
						</div>
					</div>
					<span className="bg-secondary/10 text-secondary hover:bg-secondary/20 rounded-md px-2 py-1 text-xs font-medium transition-colors">
						{mode === 'dark' ? t('theme_light') : t('theme_dark')}
					</span>
				</button>
			</fetcher.Form>
		)
	}

	const colClass = cn(
		'col-span-12 mt-3 flex w-full flex-row items-center gap-4 rounded-lg border border-border bg-card shadow-md duration-200 hover:cursor-pointer hover:bg-foreground/5 md:col-span-6 lg:col-span-4',
		wide === '2'
			? 'md:col-span-6 lg:col-span-6'
			: wide === '3' && 'md:col-span-12 lg:col-span-12',
	)

	return (
		<fetcher.Form
			method="POST"
			action="/api/set-theme"
			className={colClass}
			{...getFormProps(form)}
		>
			<input type="hidden" name="theme" value={nextMode} />
			<button
				className="flex h-full w-full cursor-pointer flex-row items-center gap-4 rounded-lg p-6"
				title={t('action_change_theme')}
			>
				<div className="relative h-10 w-10 shrink-0 text-slate-500">
					<Icon
						name={getIconName(mode === 'dark' ? 'sun' : 'moon')}
						size="xl"
					/>
				</div>
				<div className="text-start">
					<Text as="h4" className="leading-6">
						{mode === 'dark' ? t('theme_light') : t('theme_dark')}
					</Text>
					<Text as="sm" color="light">
						{mode === 'dark'
							? t('action_change_to_light_mode')
							: t('action_change_to_dark_mode')}
					</Text>
				</div>
			</button>
		</fetcher.Form>
	)
}
